import { useState } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { useListboxContext } from '@frontend/design-system';

interface ListBoxOptionGetterProps {
  'aria-checked'?: boolean;
  'aria-disabled'?: boolean;
}

interface Props extends ListBoxOptionGetterProps {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLLIElement>) => void;
  value: string;
}

export const ListBoxOption = ({ children, onClick, value, ...rest }: Props) => {
  const { getOptionProps } = useListboxContext();
  const props = getOptionProps({ disabled: rest['aria-disabled'] || false, value });

  const isDisabled = props['aria-disabled'];
  const isSelected = props['aria-selected'] && !isDisabled;

  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldAllowHover, setShouldAllowHover] = useState(true);

  const handleAnimationStart = () => {
    // Disable hover until mouse leaves and re-enters
    setShouldAllowHover(false);
    setIsAnimating(true);
  };

  return (
    <motion.li
      css={optionStyles(isAnimating || !shouldAllowHover, isDisabled)}
      initial={false}
      variants={motionVariants}
      animate={isDisabled ? 'disabled' : isSelected ? 'selected' : 'notSelected'}
      onAnimationStart={handleAnimationStart}
      onAnimationComplete={() => setIsAnimating(false)}
      onMouseLeave={() => setShouldAllowHover(true)}
      onClick={onClick}
      {...props}
    >
      {isDisabled ? (
        <Icon name='block' size={16} color='disabled' />
      ) : (
        <motion.div
          initial={false}
          variants={iconVariants}
          animate={isSelected ? 'selected' : 'notSelected'}
          css={{ flexShrink: 0 }}
        >
          <Icon name={isDisabled ? 'block' : 'check-small'} size={16} />
        </motion.div>
      )}
      <span>{children}</span>
    </motion.li>
  );
};

const optionStyles = (showHoverStyles = false, isDisabled = false) =>
  css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: showHoverStyles ? undefined : `${theme.colors.neutral5} !important`,
    },
    '&[data-focused="true"]': {
      backgroundColor: `${theme.colors.neutral5} !important`,
    },
  });

const motionVariants = {
  selected: {
    backgroundColor: [theme.colors.success10, theme.colors.white],
    transition: {
      duration: 1.5,
      ease: [0.6, 0, 0.2, 1],
      times: [0.5, 1],
    },
  },
  notSelected: {
    backgroundColor: theme.colors.white,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  disabled: {
    backgroundColor: theme.colors.white,
  },
};

const iconVariants = {
  selected: {
    color: [theme.colors.success50, theme.colors.primary50],
    opacity: [1, 1],
    transition: {
      duration: 1.5,
      ease: [0.6, 0, 0.2, 1],
      times: [0.5, 1],
    },
  },
  notSelected: {
    color: theme.colors.white,
    opacity: 0,
  },
};
